/**
 * The persisted browser anonymous id used for signup analytics and public
 * feature-flag rollout bucketing. Both the Hydra shell and the embedded app
 * read the same localStorage key, so a visitor buckets identically whichever
 * app serves the funnel.
 */
export const SIGNUP_ANONYMOUS_ID_STORAGE_KEY = 'cio:anonymous-id';

// Holds the id for this page load when localStorage can't (private mode /
// storage disabled). Without it, every call in the fallback path would mint a
// fresh id, so the several events in one visit would each carry a different
// `anon_id` and anon→user stitching would break.
let volatileAnonymousId: string | undefined;

export function getOrCreateSignupAnonymousId(): string {
  try {
    const existing = localStorage.getItem(SIGNUP_ANONYMOUS_ID_STORAGE_KEY);
    if (existing != null && existing !== '') {
      return existing;
    }

    const id = crypto.randomUUID();
    localStorage.setItem(SIGNUP_ANONYMOUS_ID_STORAGE_KEY, id);
    return id;
  } catch {
    volatileAnonymousId ??= crypto.randomUUID();
    return volatileAnonymousId;
  }
}

/**
 * Discard the anonymous id after it's been aliased to a real user, mirroring
 * Ember's `discardAnonId`: the pre-auth events are stitched to the user, and
 * keeping the id would wrongly attribute the *next* visitor on this browser
 * (e.g. a second teammate signing up) to the first user's anonymous history.
 */
export function clearSignupAnonymousId(): void {
  volatileAnonymousId = undefined;
  try {
    localStorage.removeItem(SIGNUP_ANONYMOUS_ID_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing persisted to clear.
  }
}
